<template>
  <div class="admin-home">
    <!-- 顶部导航 -->
    <header class="header">
      <span class="header-title">{{ $t('admin.Dashboard.line5_text') }}</span>
      <button class="header-icon" @click="handleLogout">🚪</button>
    </header>

    <!-- 用户信息卡片 -->
    <div class="user-card">
      <div class="user-avatar">👤</div>
      <div class="user-name">{{ userName }}</div>
      <div class="user-level">{{ $t('admin.Dashboard.line13_text') }}</div>
    </div>

    <!-- 统计数据 -->
    <div class="stats-row">
      <div class="stat-card" v-for="stat in statsCards" :key="stat.label">
        <span class="stat-num">{{ stat.value }}</span>
        <span class="stat-label">{{ stat.label }}</span>
      </div>
    </div>

    <!-- 功能菜单 -->
    <div class="function-grid">
      <div
        v-for="item in functionItems"
        :key="item.id"
        class="function-item"
        @click="handleFunction(item)"
      >
        <div :class="['function-icon', item.color]">{{ item.icon }}</div>
        <span class="function-name">{{ item.name }}</span>
        <span v-if="item.badge" class="function-badge">{{ item.badge }}</span>
      </div>
    </div>

    <!-- 最近动态 -->
    <div class="section">
      <div class="section-header">
        <h3>{{ $t('admin.Dashboard.line41_text') }}</h3>
        <a @click="viewAll">{{ $t('admin.Dashboard.line42_text') }}</a>
      </div>
      <div class="activity-list">
        <div
          v-for="activity in recentActivities"
          :key="activity.id"
          class="activity-item"
        >
          <div class="activity-icon">{{ activity.icon }}</div>
          <div class="activity-content">
            <div class="activity-text">{{ activity.text }}</div>
            <div class="activity-time">{{ activity.time }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- 待处理事项 -->
    <div class="section" v-if="todoList.length > 0">
      <div class="section-header">
        <h3>{{ $t('admin.Dashboard.line62_text') }}</h3>
      </div>
      <div class="todo-list">
        <div
          v-for="todo in todoList"
          :key="todo.id"
          class="todo-item"
          @click="handleTodo(todo)"
        >
          <div class="todo-icon">{{ todo.icon }}</div>
          <div class="todo-content">
            <div class="todo-title">{{ todo.title }}</div>
            <div class="todo-desc">{{ todo.desc }}</div>
          </div>
          <div class="todo-action">→</div>
        </div>
      </div>
    </div>
    <div class="section" v-else>
      <div class="empty-state">
        <div class="empty-icon">✅</div>
        <div class="empty-text">{{ $t('admin.Dashboard.line83_text') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { logout } from '@/utils/auth';
import { getAdminStatsOverview, getRecentActivities } from '@/api/admin';

export default {
  name: 'AdminHome',
  data() {
    return {
      functionItems: [
        { id: 'users', name: '用户管理', icon: '👥', color: 'blue' },
        { id: 'suppliers', name: '批发商管理', icon: '📦', color: 'orange' },
        { id: 'stalls', name: '档口管理', icon: '🏪', color: 'green' },
        { id: 'regions', name: '区域管理', icon: '📍', color: 'cyan' },
        { id: 'category-audit', name: '分类审核', icon: '🏷️', color: 'cyan', badge: null },
        { id: 'tax-rates', name: '税率管理', icon: '📊', color: 'cyan' },
        { id: 'roles', name: '角色管理', icon: '🔑', color: 'purple' },
        { id: 'features', name: '功能管理', icon: '⚡', color: 'orange' },
        { id: 'share-campaigns', name: '分享活动', icon: '🎁', color: 'green' },
        { id: 'share-rewards', name: '奖品审核', icon: '🎁', color: 'orange', badge: null },
        { id: 'app-version', name: 'APP版本', icon: '📱', color: 'blue' }
      ],
      // 统计数据
      stats: {
        customerCount: 0,
        wholesalerCount: 0,
        todayOrderCount: 0,
        todayRevenue: 0,
        totalOrderCount: 0,
        totalRevenue: 0,
        pendingCategoryAuditCount: 0,
        pendingAfterSalesCount: 0,
        pendingOrderCount: 0
      },
      recentActivities: [],
      todoList: []
    };
  },
  computed: {
    ...mapGetters('auth', ['userName']),
    statsCards() {
      return [
        { label: '档口数量', value: this.formatNumber(this.stats.customerCount) },
        { label: '批发商', value: this.formatNumber(this.stats.wholesalerCount) },
        { label: '今日订单', value: this.formatNumber(this.stats.todayOrderCount) },
        { label: '今日成交额', value: this.formatMoney(this.stats.todayRevenue) }
      ];
    }
  },
  mounted() {
    this.fetchStats();
    this.fetchActivities();
  },
  methods: {
    /**
     * 获取统计数据
     */
    async fetchStats() {
      try {
        const data = await getAdminStatsOverview();
        console.log('[Admin Dashboard] 统计数据:', data);
        this.stats = data;

        // 更新分类审核角标
        const categoryItem = this.functionItems.find(i => i.id === 'category-audit');
        if (categoryItem) {
          categoryItem.badge = this.stats.pendingCategoryAuditCount > 0 ? this.stats.pendingCategoryAuditCount : null;
        }

        // 构建待处理事项
        this.todoList = [];
        if (this.stats.pendingCategoryAuditCount > 0) {
          this.todoList.push({
            id: 1,
            icon: '🏷️',
            title: '待审核分类',
            desc: `${this.stats.pendingCategoryAuditCount} 个分类申请等待审核`
          });
        }
        if (this.stats.pendingAfterSalesCount > 0) {
          this.todoList.push({
            id: 2,
            icon: '🔄',
            title: '待处理售后',
            desc: `${this.stats.pendingAfterSalesCount} 笔售后申请需要处理`
          });
        }
        // 分享活动待审核奖励
        try {
          const shareStats = await this.$api.get('/admin/share/stats');
          const shareData = shareStats.data || shareStats || {};
          if (shareData.pendingCount > 0) {
            this.todoList.push({
              id: 3,
              icon: '🎁',
              title: '待审核分享奖励',
              desc: `${shareData.pendingCount} 笔分享奖励需要审核`
            });
            // 更新奖励审核角标
            const rewardItem = this.functionItems.find(i => i.id === 'share-rewards');
            if (rewardItem) {
              rewardItem.badge = shareData.pendingCount > 99 ? '99+' : String(shareData.pendingCount);
            }
          }
        } catch (e) {
          // 忽略分享统计加载失败
        }
      } catch (err) {
        console.error('[Admin Dashboard] 获取统计数据失败:', err);
      }
    },

    /**
     * 获取最近动态
     */
    async fetchActivities() {
      try {
        const activities = await getRecentActivities();
        console.log('[Admin Dashboard] 最近动态:', activities);
        this.recentActivities = (activities || []).slice(0, 8).map(a => ({
          ...a,
          time: this.formatTimeAgo(a.time)
        }));
      } catch (err) {
        console.error('[Admin Dashboard] 获取最近动态失败:', err);
      }
    },

    /**
     * 格式化时间（相对时间）
     */
    formatTimeAgo(timeStr) {
      if (!timeStr) return '';
      const now = new Date();
      const time = new Date(timeStr.replace(' ', 'T'));
      const diff = now - time;

      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return '刚刚';
      if (minutes < 60) return `${minutes} 分钟前`;
      if (hours < 24) return `${hours} 小时前`;
      if (days < 7) return `${days} 天前`;
      return timeStr.substring(0, 16);
    },

    /**
     * 格式化数字（千分位）
     */
    formatNumber(num) {
      if (!num && num !== 0) return '0';
      return Number(num).toLocaleString('zh-CN');
    },

    /**
     * 格式化金额
     */
    formatMoney(amount) {
      if (!amount && amount !== 0) return '0';
      return '¥' + Number(amount).toLocaleString('zh-CN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
    },
    // 退出登录
    async handleLogout() {
      console.log('[Dashboard] 点击退出');
      console.log('[Dashboard] 当前路径:', this.$route.fullPath);
      await logout();
      console.log('[Dashboard] logout 执行完成');
    },
    handleFunction(item) {
      console.log('handle function', item);
      // 根据功能项跳转路由
      const routeMap = {
        users: '/admin/users',
        suppliers: '/admin/suppliers',
        stalls: '/admin/stalls',
        regions: '/admin/regions',
        'category-audit': '/admin/category-audit',
        'tax-rates': '/admin/tax-rates',
        roles: '/admin/roles',
        features: '/admin/features',
        'share-campaigns': '/admin/share-campaigns',
        'share-rewards': '/admin/share-rewards',
        'app-version': '/admin/app-version'
      };
      const targetRoute = routeMap[item.id];
      if (targetRoute) {
        this.$router.push(targetRoute);
      }
    },
    handleTodo(todo) {
      console.log('handle todo', todo);
      // 根据待处理事项类型跳转到对应页面
      const todoRouteMap = {
        '待审核分类': '/admin/category-audit',
        '待处理售后': '/admin/after-sales',
        '待审核分享奖励': '/admin/share-rewards'
      };
      const route = todoRouteMap[todo.title];
      if (route) {
        this.$router.push(route);
      }
    },
    viewAll() {
      console.log('view all activities');
    }
  }
};
</script>

<style scoped>
.admin-home {
  min-height: 100vh;
  background: #f5f5f5;
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
}

.header-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border: none;
  font-size: 18px;
  cursor: pointer;
  border-radius: 50%;
}

.user-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 24px 16px;
  text-align: center;
}

.user-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 12px;
}

.user-name {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 4px;
}

.user-level {
  font-size: 12px;
  opacity: 0.9;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  padding: 12px;
}

.stat-card {
  background: #fff;
  padding: 16px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-num {
  font-size: 20px;
  font-weight: 700;
  color: #667eea;
}

.stat-label {
  font-size: 12px;
  color: #666;
}

.function-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 12px;
}

.function-item {
  background: #fff;
  padding: 16px 8px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  position: relative;
}

.function-item:active {
  transform: scale(0.95);
}

.function-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.function-icon.blue { background: #E3F2FD; color: #2196F3; }
.function-icon.green { background: #E8F5E9; color: #4CAF50; }
.function-icon.orange { background: #FFF3E0; color: #FF9800; }
.function-icon.purple { background: #F3E5F5; color: #9C27B0; }
.function-icon.cyan { background: #E0F7FA; color: #00BCD4; }

.function-name {
  font-size: 12px;
  color: #333;
  text-align: center;
}

.function-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: #F44336;
  color: #fff;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 10px;
  font-weight: bold;
}

.section {
  margin: 12px;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-header h3 {
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.section-header a {
  color: #667eea;
  font-size: 12px;
  cursor: pointer;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.activity-item:last-child {
  border: none;
  padding-bottom: 0;
}

.activity-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.activity-content {
  flex: 1;
}

.activity-text {
  font-size: 13px;
  color: #333;
  margin-bottom: 4px;
}

.activity-time {
  font-size: 11px;
  color: #999;
}

.todo-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.todo-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 8px;
  cursor: pointer;
}

.todo-item:active {
  background: #f0f0f0;
}

.todo-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.todo-content {
  flex: 1;
}

.todo-title {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.todo-desc {
  font-size: 11px;
  color: #999;
}

.todo-action {
  font-size: 18px;
  color: #999;
}

.empty-state {
  text-align: center;
  padding: 24px 0;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 8px;
}

.empty-text {
  font-size: 13px;
  color: #999;
}
</style>


